import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';
import PublicBlogNav from '../components/PublicBlogNav';
import BackToTop from '../components/BackToTop';
import { authAPI, blogAPI } from '../services/api';
import { User } from '../types';

interface BlogPostItem {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  cover_image_url: string | null;
  category: string;
  tags: string | null;
  author_id: string | null;
  is_published: boolean;
  created_at: string | null;
  updated_at: string | null;
}

const BlogList: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [posts, setPosts] = useState<BlogPostItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    const loadData = async () => {
      // User fetch is optional — don't let it block blog loading
      if (localStorage.getItem('access_token')) {
        try {
          const userRes = await authAPI.getCurrentUser();
          setUser(userRes.data);
        } catch {
          // Not logged in or token expired — show public nav
        }
      }

      try {
        const [postsRes, catsRes] = await Promise.all([
          blogAPI.list(),
          blogAPI.getCategories(),
        ]);
        setPosts(postsRes.data);
        setCategories(catsRes.data);
      } catch (err) {
        console.error('Failed to load blog posts:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const filteredPosts = selectedCategory
    ? posts.filter((p) => p.category === selectedCategory)
    : posts;

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-800">
      {/* SEO — React 19 hoists to <head> */}
      <title>Blog — NWC-Analytics | Stock Analysis Insights &amp; Guides</title>
      <meta name="description" content="Expert stock analysis articles, technical indicator guides, portfolio strategies, and market insights for retail investors." />
      <link rel="canonical" href="https://nwc-analytics.com/blogs" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Blog — NWC-Analytics" />
      <meta property="og:description" content="Expert stock analysis articles, technical indicator guides, and market insights." />
      <meta property="og:url" content="https://nwc-analytics.com/blogs" />
      <meta property="og:image" content="https://nwc-analytics.com/images/og-default.png" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Blog — NWC-Analytics" />
      <meta name="twitter:description" content="Expert stock analysis articles and market insights for retail investors." />
      {/* Nav — full navbar for logged-in users, soft-sell for public */}
      {user ? (
        <NavBar currentPage="blogs" user={user} onLogout={handleLogout} />
      ) : (
        <PublicBlogNav />
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-3">Blog</h1>
        </div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === null
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Posts */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-20">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No posts yet</h2>
            <p className="text-gray-500 dark:text-gray-400">Check back soon for new content.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blogs/${post.slug}`}
                className="group bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow-sm hover:shadow-lg border border-gray-200 dark:border-gray-700 transition-all duration-200 hover:-translate-y-0.5"
              >
                {/* Cover Image */}
                {post.cover_image_url ? (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={post.cover_image_url}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/9] bg-gradient-to-br from-primary-500/20 to-primary-700/20 flex items-center justify-center">
                    <svg className="w-12 h-12 text-primary-400/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                    </svg>
                  </div>
                )}

                <div className="p-5">
                  {/* Category + Date */}
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                      {post.category}
                    </span>
                    {post.created_at && (
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {new Date(post.created_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                        })}
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors line-clamp-2">
                    {post.title}
                  </h2>

                  {/* Excerpt */}
                  {post.excerpt && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                      {post.excerpt}
                    </p>
                  )}

                  {/* Tags */}
                  {post.tags && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {post.tags.split(',').slice(0, 3).map((tag, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                          {tag.trim()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-gray-700 mt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/images/logo.png" alt="NWC-Analytics," className="h-6 w-6" />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                © {new Date().getFullYear()} NWC-Analytics, LLC
              </span>
            </div>
          </div>
        </div>
      </footer>
      <BackToTop />
    </div>
  );
};

export default BlogList;